
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {CounterInfo} from './CounterInfo';
import {FieldModelCounterInfo} from './CounterInfo';

/**
 * SymbolCounterInfo struct
 */
class SymbolCounterInfo {
  /**
   * Initialize struct
   * @param {!UUID=} symbolId
   * @param {!CounterInfo=} counterInfo
   * @constructor
   */
  constructor (symbolId = new UUID(), counterInfo = new CounterInfo()) {
    this.SymbolId = symbolId
    this.CounterInfo = counterInfo
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SymbolCounterInfo}
   * @param {!SymbolCounterInfo} other Other struct
   * @returns {!SymbolCounterInfo} This struct
   */
  copy (other) {
    if (other.SymbolId != null) {
      this.SymbolId = new UUID(other.SymbolId)
    } else {
      this.SymbolId = null
    }
    if (other.CounterInfo != null) {
      this.CounterInfo = CounterInfo.fromObject(other.CounterInfo)
    } else {
      this.CounterInfo = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SymbolCounterInfo}
   * @returns {!SymbolCounterInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SymbolCounterInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SymbolCounterInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SymbolCounterInfo}
   * @param {!SymbolCounterInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SymbolCounterInfo)) {
      throw new TypeError('Instance of SymbolCounterInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SymbolCounterInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      SymbolId: ((this.SymbolId != null) ? this.SymbolId.toString() : null),
      CounterInfo: ((this.CounterInfo != null) ? this.CounterInfo : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SymbolCounterInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SymbolCounterInfo} other Object value
   * @returns {!SymbolCounterInfo} Created struct
   */
  static fromObject (other) {
    return new SymbolCounterInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SymbolCounterInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SymbolCounterInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SymbolCounterInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 75
  }
}

export { SymbolCounterInfo };

/**
 * Fast Binary Encoding SymbolCounterInfo field model
 */
class FieldModelSymbolCounterInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.SymbolId = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.CounterInfo = new FieldModelCounterInfo(buffer, this.SymbolId.fbeOffset + this.SymbolId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSymbolCounterInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSymbolCounterInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.SymbolId.fbeSize + this.CounterInfo.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSymbolCounterInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.SymbolId.fbeExtra + this.CounterInfo.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSymbolCounterInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSymbolCounterInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSymbolCounterInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 75
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSymbolCounterInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSymbolCounterInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolId.fbeSize

    if ((fbeCurrentSize + this.CounterInfo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CounterInfo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CounterInfo.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSymbolCounterInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSymbolCounterInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSymbolCounterInfo}
   * @param {!SymbolCounterInfo} fbeValue Default value, defaults is new SymbolCounterInfo()
   * @returns {!SymbolCounterInfo} SymbolCounterInfo value
   */
  get (fbeValue = new SymbolCounterInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSymbolCounterInfo}
   * @param {!SymbolCounterInfo} fbeValue SymbolCounterInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolId.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolId = this.SymbolId.get()
    } else {
      fbeValue.SymbolId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolId.fbeSize

    if ((fbeCurrentSize + this.CounterInfo.fbeSize) <= fbeStructSize) {
      fbeValue.CounterInfo = this.CounterInfo.get()
    } else {
      fbeValue.CounterInfo = new CounterInfo()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CounterInfo.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSymbolCounterInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSymbolCounterInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSymbolCounterInfo}
   * @param {!SymbolCounterInfo} fbeValue SymbolCounterInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSymbolCounterInfo}
   * @param {SymbolCounterInfo} fbeValue SymbolCounterInfo value
   */
  setFields (fbeValue) {
    this.SymbolId.set(fbeValue.SymbolId)
    this.CounterInfo.set(fbeValue.CounterInfo)
  }
}

export { FieldModelSymbolCounterInfo };

/**
 * Fast Binary Encoding SymbolCounterInfo model
 */
class SymbolCounterInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSymbolCounterInfo(this.buffer, 4)
  }

  /**
   * Get the SymbolCounterInfo model
   * @this {!SymbolCounterInfoModel}
   * @returns {!FieldModelSymbolCounterInfo} model SymbolCounterInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SymbolCounterInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SymbolCounterInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SymbolCounterInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SymbolCounterInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSymbolCounterInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SymbolCounterInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SymbolCounterInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SymbolCounterInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SymbolCounterInfoModel}
   * @param {!SymbolCounterInfo} value SymbolCounterInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SymbolCounterInfoModel}
   * @param {!SymbolCounterInfo} value SymbolCounterInfo value, defaults is new SymbolCounterInfo()
   * @return {!object} Deserialized SymbolCounterInfo value and its size
   */
  deserialize (value = new SymbolCounterInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SymbolCounterInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SymbolCounterInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SymbolCounterInfoModel}
   * @param {!number} prev Previous SymbolCounterInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SymbolCounterInfoModel };
